import React, { useState } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { TriggerNode } from "../../../../views/convoauto-pages/bot-studio/types";
import { RightDot } from "../../../shared/action-core";
import { NodeMenu } from "../../../shared/trigger-shared/node-menu";
import { DeleteModal } from "../../../shared/trigger-shared/delete-modal";

const INTERVAL_OPTIONS = [
  { label: "Daily", value: "Days" },
  { label: "Weekly", value: "Weeks" },
  { label: "Monthly", value: "Months" },
  { label: "Once", value: "Once" },
];

interface RunAtSpecificTimeTriggerCardProps {
  node: TriggerNode;
  onChange: (n: TriggerNode) => void;
  onDelete: () => void;
  onReset: () => void;
}

export const RunAtSpecificTimeTriggerCard: React.FC<RunAtSpecificTimeTriggerCardProps> = ({
  node,
  onChange,
  onDelete,
  onReset,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const update = (patch: Partial<TriggerNode>) => onChange({ ...node, ...patch });

  return (
    <>
      <div className="relative">
        <div className="absolute -top-7 left-0">
          <span className="bg-gray-800 text-white text-xs px-3 py-1 rounded-full">
            Bot Starting Point
          </span>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 w-[270px] relative">
          <RightDot />
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide">
              Trigger
            </span>
            <NodeMenu onDelete={() => setShowDeleteModal(true)} onReset={onReset} />
          </div>

          <p className="text-indigo-600 font-semibold text-sm mb-0.5">
            Run at Specific Time
          </p>
          <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-4">
            Fires once at a specific date and time. No inbound message is required.
          </p>

          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Date and Time
          </label>
          <div className="relative mb-3">
            <input
              type="datetime-local"
              value={(node.fixedTime as string) ?? ""}
              onChange={(e) => update({ fixedTime: e.target.value })}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-indigo-300 pr-9"
            />
            <CalendarDays size={17} className="absolute right-3 top-2.5 text-gray-500 dark:text-gray-400 pointer-events-none" />
          </div>

          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Repeat Every
          </label>
          <div className="relative mb-3">
            <select
              value={(node.triggerInterval as string) ?? "Days"}
              onChange={(e) => update({ triggerInterval: e.target.value })}
              className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            >
              {INTERVAL_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute right-2 top-2.5 pointer-events-none">
              <ChevronDown size={16} className="text-gray-500 dark:text-gray-400" />
            </div>
          </div>

          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Recipients
          </label>
          <textarea
            value={(node.recipients as string) ?? ""}
            onChange={(e) => update({ recipients: e.target.value })}
            rows={4}
            placeholder="Enter phone numbers (one number per line)"
            className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 outline-none focus:ring-1 focus:ring-indigo-300 resize-none"
          />
        </div>
      </div>

      {showDeleteModal && (
        <DeleteModal
          title="Delete trigger?"
          description="This will remove the trigger and any connected flow from your bot journey. This action cannot be undone."
          confirmLabel="Delete Trigger"
          onConfirm={() => { onDelete(); setShowDeleteModal(false); }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};